<script>
  import { mapGetters, mapActions } from 'vuex';
  import { useAlert } from 'dashboard/composables';

  export default {
    props: {
      isEditingSchedule: {
        type: Boolean,
        default: false,
      },
      branchId: {
        type: String,
        default: null,
      },
      editScheduleId: {
        type: String,
        default: null,
      },
      resetCreateSchedule: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        scheduleName: '',
        workingHours: [{
          "day_of_week": 0,
          "end_time": "17:00:00",
          "start_time": "09:00:00"
        }],
        // i18n: day names
        days: [
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.SUNDAY'),
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.MONDAY'),
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.TUESDAY'),
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.WEDNESDAY'),
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.THURSDAY'),
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.FRIDAY'),
          this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.DAYS_OF_WEEK.SATURDAY')
        ],
        selectedTimezone: '',
        timezones: null,
      }
    },
    computed: {
      ...mapGetters('appointments', ['selectedSchedule']),
    },
    methods: {
      ...mapActions('appointments', [
        'createSchedule',
        'editSchedule',
        'fetchSchedule',
        'fetchScheduleList',
        'createWorkingHours',
        'getTimezones',
      ]),

      async handleCreateSchedule() {
        if (!this.scheduleName || this.selectedTimezone === '') {
          useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.ALERT_FILL_FIELDS'));
          return;
        }
        const payload = {
          "name": this.scheduleName,
          "timezone": this.selectedTimezone
        }
        try {
          const scheduleId = await this.createSchedule({ branchId: this.branchId, payload });
          await Promise.all(
            this.workingHours.map(wh => this.createWorkingHours({ scheduleId: scheduleId, payload: wh }))
          );
          this.$emit('created', scheduleId);
          await this.fetchScheduleList(this.branchId);
          
          // i18n: success message
          useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.SCHEDULE_CREATED'));
          this.$store.dispatch('onboarding/completeLocalTask', 'setup_staff_schedule');
          if(this.$route.query.onboarding === 'true') {
            this.$router.push(this.$route.path);
          }
          this.resetCreateSchedule();
        } catch (err) {
          console.error(err);
          // i18n: error message
          useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.ERROR_MESSAGE'));
        }
      },

      async handleEditSchedule() {
        if (!this.scheduleName) {
          // i18n: alert message
          useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.ALERT_FILL_FIELDS'));
          return;
        }
        const payload = {
          "name": this.scheduleName,
          "is_active": true,
        }
        try {
          await this.editSchedule({ scheduleId: this.editScheduleId, payload });
          await this.fetchScheduleList(this.branchId);
          // i18n: success message
          useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.SCHEDULE_EDITED'));
          this.resetCreateSchedule();
        } catch (err) {
          console.error(err);
          // i18n: error message
          useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.ERROR_MESSAGE'));
        }
      },

      addWorkingHour() {
        this.workingHours.push({ day: null, start_time: '', end_time: '' });
      },

      formatTimezone(timezone, timestamp) {
        const cityRaw = timezone?.split?.("/")[1] || timezone;
        const city = cityRaw?.replace(/_/g, " ") || "";

        if (!timestamp) return city;

        const match = timestamp.match(/([+-]\d{2}:\d{2})$/);
        const offset = match ? match[1] : "+00:00";
        return `${city} (UTC${offset})`;
      },

      removeWorkingHour(index) {
        this.workingHours.splice(index, 1);
      },

      handleTest() {
        console.log(this.workingHours);
      },

      isDaySelected(day, currentIndex) {
        return this.workingHours.some((wh, i) => wh.day_of_week === day && i !== currentIndex);
      },
    },

    async mounted() {
      try {
        this.timezones = await this.getTimezones();
        if (this.isEditingSchedule) {
          await this.fetchSchedule(this.editScheduleId);
          this.scheduleName = this.selectedSchedule.name;
          this.selectedTimezone = this.selectedSchedule.timezone;
        }
      } catch (err) {
        console.error(err);
        // i18n: error message
        useAlert(this.$t('BRANCHES.CREATE_SCHEDULE_MODAL.ERROR_MESSAGE'));
      }
    }
  }
</script>

<template>
  <div class="p-6 border-2 rounded-lg bg-white dark:bg-slate-800 shadow-lg max-w-md w-full mx-auto">
    <label class="block mb-4">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('BRANCHES.CREATE_SCHEDULE_MODAL.TITLE') }}</span>
      <input 
        type="text" 
        v-model="scheduleName" 
        :placeholder="$t('BRANCHES.CREATE_SCHEDULE_MODAL.NAME_PLACEHOLDER')"
        class="mt-1 w-full min-w-[250px] p-3 border rounded-md text-gray-900 dark:text-white dark:bg-slate-700 dark:border-gray-600 focus:ring-2 focus:ring-woot-500 focus:outline-none"
      />
    </label>

    <div class="block" v-if="!isEditingSchedule">
      <span class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ $t('BRANCHES.CREATE_SCHEDULE_MODAL.TIMEZONE_LABEL') }}</span>
      <multiselect 
        v-model="selectedTimezone"
        :options="Object.keys(timezones)"
        :custom-label="(key) => formatTimezone(key, timezones[key])"
        :placeholder="$t('BRANCHES.CREATE_SCHEDULE_MODAL.TIMEZONE_PLACEHOLDER')"
        :searchable="true"
        :close-on-select="true"
        :show-labels="false"
        class="w-full text-gray-900 dark:text-white dark:bg-slate-700"
      />
    </div>

    <div class="flex justify-between mt-4 gap-4">
      <button 
        @click="resetCreateSchedule()"
        class="w-full py-2 mt-2 text-white font-medium rounded-md transition 
          bg-slate-600 hover:bg-slate-700 focus:ring-2 focus:ring-slate-400 focus:outline-none"
      >
        {{ $t('BRANCHES.CREATE_SCHEDULE_MODAL.CANCEL_BUTTON') }}
      </button>
      <button 
        @click="isEditingSchedule ? handleEditSchedule() : handleCreateSchedule()"
        class="w-full py-2 mt-2 text-white font-medium rounded-md transition 
          bg-woot-600 hover:bg-woot-700 focus:ring-2 focus:ring-woot-400 focus:outline-none"
      >
        {{ isEditingSchedule ? $t('BRANCHES.CREATE_SCHEDULE_MODAL.EDIT_BUTTON') : $t('BRANCHES.CREATE_SCHEDULE_MODAL.CREATE_BUTTON') }}
      </button>
    </div>
  </div>
</template>
